import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { TrendDown01Icon, TrendUp01Icon } from '@untitled-theme/icons-react'
import { useState } from 'react'

import { housePartsIcons } from '@/assets/icons/house-parts'
import CustomCheckbox from '@/shared/components/CustomCheckbox'
import { StyledTooltip } from '@/shared/components/StyledTooltip'
import type { RetrofitName, RetrofitState } from '@/shared/types'

import type { MeasuresTableProps, SortDirection, SortField } from './MeasuresTable.types'
import { getRetrofitDisplayName, retrofitToIcon } from './MeasuresTable.types'

const MeasuresTable = ({
  measures,
  selectedMeasureIds = [],
  onSelectionChange,
  onMeasureClick,
}: MeasuresTableProps) => {
  const [sortField, setSortField] = useState<SortField>('amortization')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === 'asc'
    setSortDirection(isAsc ? 'desc' : 'asc')
    setSortField(field)
  }

  const handleSelectMeasure = (retrofitName: RetrofitName) => {
    const newSelection = selectedMeasureIds.includes(retrofitName)
      ? selectedMeasureIds.filter(selectedId => selectedId !== retrofitName)
      : [...selectedMeasureIds, retrofitName]
    onSelectionChange?.(newSelection)
  }

  // Helper functions to extract data from RetrofitState
  const getSavingsPotentialCurrency = (measure: RetrofitState): number => {
    // Calculate savings from energy costs - simplified version
    // In reality, this would compare current state vs retrofit state energy costs
    const currentYearlyCost = measure.economics.avgEnergyCostSavingsInCtWithinFiveYears
    return currentYearlyCost / 100 // Convert cents to euros
  }

  const getSavingsPctEnergy = (measure: RetrofitState): number => {
    return measure.economics.avgEnergyCostSavingsInPctWithinFiveYears
  }

  const getAmortization = (measure: RetrofitState): number => {
    return measure.economics.amortizationTimeInYears || 0
  }

  const getRenovationCosts = (measure: RetrofitState): number => {
    return measure.economics.retrofitCostsInCt?.mean
      ? measure.economics.retrofitCostsInCt.mean / 100
      : 0
  }

  const sortedMeasures = [...measures].sort((a, b) => {
    if (sortField === 'retrofitName') {
      const aName = getRetrofitDisplayName(a.retrofitName)
      const bName = getRetrofitDisplayName(b.retrofitName)
      return sortDirection === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName)
    }

    if (sortField === 'savingsPotential') {
      const aValue = getSavingsPotentialCurrency(a)
      const bValue = getSavingsPotentialCurrency(b)
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }

    if (sortField === 'amortization') {
      const aValue = getAmortization(a)
      const bValue = getAmortization(b)
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }

    if (sortField === 'renovationCosts') {
      const aValue = getRenovationCosts(a)
      const bValue = getRenovationCosts(b)
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }

    return 0
  })

  const formatCurrency = (value: number) => {
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: '4px',
          backgroundColor: '#F5F5F7',
          padding: '3px 8px',
          borderRadius: '18px',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '10px',
            fontWeight: 400,
            color: '#464A54',
          }}
        >
          Ab
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#464A54',
          }}
        >
          {formatted}
        </Box>
      </Box>
    )
  }

  const formatSavingsPotential = (savings: { currency: number; pct: number }) => {
    const currencyLower = Math.round(savings.currency * 0.9)
    const currencyUpper = Math.round(savings.currency * 1.1)

    const pct = Math.ceil(savings.pct)

    const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num)

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'flex',
            fontSize: '12px',
            fontWeight: 600,
            color: '#464A54',
          }}
        >
          {savings.currency < 0
            ? '< 0 €'
            : `${formatNumber(currencyLower)} – ${formatNumber(currencyUpper)} €`}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginRight: '40px',
            padding: '4px 8px',
            borderRadius: '16px',
            backgroundColor: pct < 0 ? '#FDEDED' : '#E5F5E7',
          }}
        >
          {pct < 0 ? (
            <TrendUp01Icon style={{ width: 18, height: 18, color: '#C62828' }} />
          ) : (
            <TrendDown01Icon style={{ width: 18, height: 18, color: '#6B9070' }} />
          )}
          <Box
            component="span"
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#464A54',
            }}
          >
            {pct < 0 ? `< 0 %` : `~ ${pct} %`}
          </Box>
        </Box>
      </Box>
    )
  }

  const formatYears = (value: number) => {
    if (!value) return 'k. A.'
    if (value > 40) return '+40 Jahre'
    return `${value} Jahre`
  }

  return (
    <TableContainer
      sx={{
        border: '1px solid #E1E1E4',
        borderRadius: '16px',
        overflow: 'auto',
      }}
    >
      <Table sx={{ tableLayout: 'auto', width: 'max-content', minWidth: '100%' }}>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: '#FAFAFB',
            }}
          >
            <TableCell
              sx={{
                fontWeight: 500,
                fontSize: '10px',
                color: '#1A1D24',
                padding: '16px 8px',
                borderBottom: '1px solid #E1E1E4',
                width: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <TableSortLabel
                active={sortField === 'retrofitName'}
                direction={sortField === 'retrofitName' ? sortDirection : 'asc'}
                onClick={() => handleSort('retrofitName')}
                sx={{
                  width: '100%',
                  justifyContent: 'space-between',
                  '& .MuiTableSortLabel-icon': {
                    marginLeft: 'auto',
                  },
                }}
              >
                Sanierungsmaßnahme
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 500,
                fontSize: '10px',
                color: '#1A1D24',
                padding: '16px 8px',
                borderBottom: '1px solid #E1E1E4',
                width: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <StyledTooltip
                title="Zur Berechnung wird der aktuelle Energieverbrauch mit dem voraussichtlichen Verbrauch nach der Sanierung verglichen. Die Energiepreise und zukünftige Entwicklungen des CO2-Preises werden dabei berücksichtigt."
                placement="bottom"
              >
                <TableSortLabel
                  active={sortField === 'savingsPotential'}
                  direction={sortField === 'savingsPotential' ? sortDirection : 'asc'}
                  onClick={() => handleSort('savingsPotential')}
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    '& .MuiTableSortLabel-icon': {
                      marginLeft: 'auto',
                    },
                  }}
                >
                  Jährliches Einsparpotenzial
                </TableSortLabel>
              </StyledTooltip>
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 500,
                fontSize: '10px',
                color: '#1A1D24',
                padding: '16px 8px',
                borderBottom: '1px solid #E1E1E4',
                width: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <StyledTooltip
                title="Beschreibt die Rückführung einer Investition durch die daraus erzielten Einsparungen. Sie gibt an, nach welcher Zeit sich die anfänglichen Kosten vollständig bezahlt gemacht haben."
                placement="bottom"
              >
                <TableSortLabel
                  active={sortField === 'amortization'}
                  direction={sortField === 'amortization' ? sortDirection : 'asc'}
                  onClick={() => handleSort('amortization')}
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    '& .MuiTableSortLabel-icon': {
                      marginLeft: 'auto',
                    },
                  }}
                >
                  Amortisation
                </TableSortLabel>
              </StyledTooltip>
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 500,
                fontSize: '10px',
                color: '#1A1D24',
                padding: '16px 8px',
                borderBottom: '1px solid #E1E1E4',
                width: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <StyledTooltip
                title="Die Sanierungskosten werden anhand wissenschaftlicher Kostenfunktionen berechnet, die wichtige Faktoren wie Dämmfläche, Dämmstärke und Anlagenleistung berücksichtigen. Anschließend werden die Ergebnisse über Regionalfaktoren an regionale Preisunterschiede in Deutschland angepasst."
                placement="bottom"
              >
                <TableSortLabel
                  active={sortField === 'renovationCosts'}
                  direction={sortField === 'renovationCosts' ? sortDirection : 'asc'}
                  onClick={() => handleSort('renovationCosts')}
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    '& .MuiTableSortLabel-icon': {
                      marginLeft: 'auto',
                    },
                  }}
                >
                  Sanierungskosten
                </TableSortLabel>
              </StyledTooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedMeasures.map((measure: RetrofitState, index: number) => {
            const displayName = getRetrofitDisplayName(measure.retrofitName)
            const icon = retrofitToIcon[measure.retrofitName]
            const savingsPotential = {
              currency: getSavingsPotentialCurrency(measure),
              pct: getSavingsPctEnergy(measure),
            }

            return (
              <TableRow
                key={measure.retrofitName}
                onClick={() => onMeasureClick?.(measure)}
                sx={{
                  cursor: onMeasureClick ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: '#F5F5F5',
                  },
                  '&:last-child td': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableCell
                  sx={{
                    padding: '16px 8px',
                    borderBottom:
                      index === sortedMeasures.length - 1 ? 'none' : '1px solid #E1E1E4',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CustomCheckbox
                      checked={selectedMeasureIds.includes(measure.retrofitName)}
                      onClick={e => {
                        e.stopPropagation()
                        handleSelectMeasure(measure.retrofitName)
                      }}
                      sx={{
                        padding: '16px',
                        margin: '-8px',
                      }}
                    />
                    {icon && (
                      <Box
                        component="img"
                        src={housePartsIcons[icon]}
                        alt={displayName}
                        sx={{
                          width: 32,
                          height: 32,
                          objectFit: 'contain',
                          margin: '0 6px',
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '12px',
                        marginLeft: '6px',
                        maxWidth: { lg: '172px' },
                        display: { lg: '-webkit-box' },
                        WebkitLineClamp: { lg: 2 },
                        WebkitBoxOrient: { lg: 'vertical' },
                        overflow: { lg: 'hidden' },
                        textOverflow: { lg: 'ellipsis' },
                        wordBreak: { lg: 'break-word' },
                        whiteSpace: { lg: 'normal' },
                      }}
                    >
                      {displayName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    padding: '16px 8px',
                    borderBottom:
                      index === sortedMeasures.length - 1 ? 'none' : '1px solid #E1E1E4',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatSavingsPotential(savingsPotential)}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#464A54',
                    padding: '16px 8px',
                    borderBottom:
                      index === sortedMeasures.length - 1 ? 'none' : '1px solid #E1E1E4',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatYears(getAmortization(measure))}
                </TableCell>
                <TableCell
                  sx={{
                    padding: '16px 8px',
                    borderBottom:
                      index === sortedMeasures.length - 1 ? 'none' : '1px solid #E1E1E4',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCurrency(getRenovationCosts(measure))}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default MeasuresTable
